import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ModuleLayout from '@/components/admin/ModuleLayout';
import { RowActionsMenu } from '@/components/admin/RowActionsMenu';
import { PencilIcon, TrashBinIcon, PlusIcon } from '@/icons';
import { cccdScoring, type EvaluationParameter } from '@/lib/api/cccdScoringClient';
import { PARAMETER_TYPE_LABELS } from '@/lib/cccdScoringParameters';
import { applyOptimistic, rollbackOptimistic } from '@/lib/optimisticMutation';
import { showUndoToast } from '@/lib/undoToast';
import { toastError } from '@/lib/toast';
import { LoadState } from "@/components/ui/LoadState"

// Biblioteca global de parámetros de evaluación (cccd-scoring-global-
// parameters) — se definen UNA sola vez aquí y se vinculan directamente a
// las categorías de cualquier evento nacional o regional desde su pestaña
// Puntaje (ScoringParametersSection), sin recrearlos ni clonarlos por
// evento/año. Editar un parámetro aquí se refleja en toda categoría que lo
// tenga vinculado.
//
// Crear/editar viven en páginas dedicadas (/cccd/scoring-parameters/new,
// /:id/edit — cccd-scoring-ux-redesign) en vez de un panel inline: el
// formulario amigable con ejemplo en vivo necesita más espacio del que cabe
// expandido en esta lista, y mantiene el mismo patrón de "crear y editar
// comparten la misma UI" que el resto de Gestión.
export default function ScoringParametersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const queryKey = ['scoring-parameters'];
  const { data: parameters = [], isLoading } = useQuery({ queryKey, queryFn: () => cccdScoring.listParameters() });

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const deleteMutation = useMutation({ mutationFn: (id: string) => cccdScoring.deleteParameter(id) });

  const handleDelete = async (parameter: EvaluationParameter) => {
    const previous = await applyOptimistic<EvaluationParameter[]>(qc, queryKey, (prev) =>
      (prev ?? []).filter((p) => p.id !== parameter.id),
    );
    showUndoToast({
      message: `Parámetro "${parameter.name}" eliminado`,
      onUndo: () => rollbackOptimistic(qc, queryKey, previous),
      onCommit: async () => {
        try {
          await deleteMutation.mutateAsync(parameter.id);
          invalidate();
        } catch {
          rollbackOptimistic(qc, queryKey, previous);
          toastError('No se pudo eliminar: puede seguir vinculado a alguna categoría.');
        }
      },
    });
  };

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, 'Parámetros de evaluación']}
      title="Parámetros de evaluación"
      subtitle="Biblioteca global reutilizable entre eventos y años — vincúlalos a las categorías que correspondan desde la pestaña Puntaje de cada evento o regional, sin recrearlos."
      tabs={[]}
      metrics={[]}
    >
      <button
        type="button"
        onClick={() => navigate('/cccd/scoring-parameters/new')}
        className="mb-5 flex w-full items-center gap-2.5 rounded-[14px] border-[1.5px] border-dashed border-gray-300 bg-gray-25 px-3.5 py-3.5 text-gray-400 transition-colors hover:border-brand-300 hover:text-brand-500 dark:border-gray-700 dark:bg-white/[0.02] dark:hover:border-brand-700"
      >
        <PlusIcon className="size-4" />
        <span className="text-[13px] font-medium">Agregar parámetro nuevo</span>
      </button>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        {isLoading ? (
          <LoadState />
        ) : parameters.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Sin parámetros todavía.</p>
        ) : (
          parameters.map((parameter) => (
            <div key={parameter.id} className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-3 last:border-b-0 dark:border-gray-800">
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">{parameter.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{PARAMETER_TYPE_LABELS[parameter.parameterType]}</p>
              </div>
              <RowActionsMenu
                actions={[
                  { label: 'Editar', icon: <PencilIcon />, to: `/cccd/scoring-parameters/${parameter.id}/edit` },
                  { label: 'Eliminar', icon: <TrashBinIcon />, onClick: () => handleDelete(parameter), variant: 'danger' },
                ]}
              />
            </div>
          ))
        )}
      </div>
    </ModuleLayout>
  );
}
